import type { StorageApiErrorBody, StorageErrorCode } from "../types/project-storage.types";

const STORAGE_ERROR_MESSAGES: Record<StorageErrorCode, string> = {
  PROJECT_NOT_FOUND: "Projeto não encontrado.",
  FOLDER_NOT_FOUND: "Pasta não encontrada.",
  FILE_NOT_FOUND: "Arquivo não encontrado.",
  FOLDER_IS_SYSTEM: "Pastas padrão do sistema não podem ser alteradas.",
  FOLDER_NOT_EMPTY: "Pasta contém itens. Confirme a exclusão em cascata.",
  FOLDER_NAME_CONFLICT: "Já existe uma pasta com este nome aqui.",
  FOLDER_MAX_DEPTH: "Limite de níveis de pasta atingido.",
  NAME_CONFLICT: "Já existe um arquivo com este nome nesta pasta.",
  FILE_TOO_LARGE: "Arquivo excede 15 MB.",
  INVALID_EXTENSION: "Tipo de arquivo não permitido.",
  INVALID_MIME: "Tipo do arquivo não corresponde à extensão.",
  BATCH_TOO_LARGE: "Envie no máximo 20 arquivos por vez.",
  FILE_NOT_FOUND_IN_STORAGE: "Falha no envio — tente novamente.",
  RATE_LIMIT_EXCEEDED: "Muitas tentativas. Aguarde um momento.",
};

export type StorageErrorScope = "upload" | "action" | "download" | "navigation";

const SCOPE_FALLBACKS: Record<StorageErrorScope, string> = {
  upload: "Não foi possível enviar o arquivo.",
  action: "Não foi possível concluir a ação.",
  download: "Não foi possível baixar o arquivo.",
  navigation: "Não foi possível carregar o repositório.",
};

export type ExtractedStorageError = StorageApiErrorBody & {
  httpStatus?: number;
};

export type MappedStorageError = {
  errorCode?: StorageErrorCode;
  message: string;
};

export function getStorageErrorMessage(
  errorCode: StorageErrorCode,
  fallback?: string
): string {
  return STORAGE_ERROR_MESSAGES[errorCode] ?? fallback ?? SCOPE_FALLBACKS.action;
}

export function extractStorageApiError(error: unknown): ExtractedStorageError {
  const response = (error as { response?: { status?: number; data?: StorageApiErrorBody } })
    ?.response;

  return {
    httpStatus: response?.status,
    ...(response?.data ?? {}),
  };
}

export function mapStorageError(
  error: unknown,
  scope: StorageErrorScope = "action",
  fallback?: string
): MappedStorageError {
  const body = extractStorageApiError(error);
  const defaultMessage = fallback ?? SCOPE_FALLBACKS[scope];

  if (body.httpStatus === 429) {
    return {
      errorCode: "RATE_LIMIT_EXCEEDED",
      message: getStorageErrorMessage("RATE_LIMIT_EXCEEDED"),
    };
  }

  if (body.errorCode) {
    return {
      errorCode: body.errorCode,
      message: getStorageErrorMessage(body.errorCode, body.message ?? defaultMessage),
    };
  }

  return { message: body.message ?? defaultMessage };
}

/** @deprecated Use getStorageErrorMessage or mapStorageError */
export function getUploadErrorMessage(
  errorCode: StorageErrorCode,
  fallback?: string
): string {
  return getStorageErrorMessage(errorCode, fallback ?? SCOPE_FALLBACKS.upload);
}
